import React, { useEffect } from "react";
import styles from "./_deposit-page.module.scss";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppState } from "../../../Store/rootReducer";
import { useAppDispatch } from "../../../Store/useAppDispatch";
import { createDownPaymentLink, loadLibrary, setClient } from "../../../Store/Shared/actions";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import MessageViewer from "../../Controls/MessageViewer/MessageViewer";
import {base64DecodeParams} from "../../../Helpers/Utilities";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import {hasValue, toDollars} from "@amsanalytics/react-shared-library";
import {getDownPaymentHistories, loadCuratedVehicleDetail} from "../../../Store/CuratedVehicles/actions";
import {Params, useNavigate} from "react-router";
import {DownPaymentLinkRequest} from "../../../Models/DownPaymentLinkRequest";
import html2canvas from 'html2canvas-pro';
import {httpFormDataAsync} from "../../../Http/HttpClient";
import {MS_PER_MINUTE} from "@progress/kendo-date-math";
import useThirdPartyChatbot from "../../../Hooks/useThirdPartyChatbot";

interface DepositPageParams {
  customerPortalLinkId: string;
  stockNumber: string;
  applicationId: string;
  depositAmount: number;
  fullDownPaymentAmount: number;
  clientId: string;
}

export interface DepositPageProps {
  headerAndFooterHeight: number;
}

// Matches the data-deposit-scroller attribute on the scrolling content container, so the
// screenshot capture can find it in both the live page and the html2canvas clone.
const SCROLLER_SELECTOR = '[data-deposit-scroller]';

export default function DepositPage (props: DepositPageProps) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const clientId = useSelector<AppState, string | null>(appState => appState.shared.clientId);
  const loadingDetail = useSelector((state: AppState) => state.curatedVehicles.loading);
  const selectedVehicleLoaded = loadingDetail.selectedVehicleLoaded;
  const selectedVehicle = useSelector((state: AppState) => state.curatedVehicles.selectedVehicle);
  const isCreatingDownPaymentLink = useSelector<AppState, boolean>(appState => appState.shared.isCreatingDownPaymentLink);
  const applicationDetails = useSelector((state: AppState) => state.curatedVehicles.application);
  const selectedFilters = applicationDetails?.selectedFilters ?? null;
  const isLoading = !selectedVehicleLoaded || isCreatingDownPaymentLink;
  const downPaymentDetails = useSelector((appState: AppState) => appState.shared.curatedSiteDownPaymentRequestDetails);
  const [step, setStep] = React.useState(0);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const downPaymentHistories = useSelector((state: AppState) => state.curatedVehicles.downPaymentHistories);

  // Hide (don't unload) the chat widget while the payment iframe is up; it comes back on Back.
  useThirdPartyChatbot(applicationDetails?.chatbotScriptUrl, step === 1);

  // Convert base64 image to File
  const base64ToFile = (base64: string, filename: string, contentType = 'image/jpeg'): File => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    return new File([blob], filename, { type: contentType });
  };

  // Uploads a screenshot of the confirmation page to the server
  const uploadScreenshot = async (base64Image: string) => {
    const filename = `Deposit_Disclaimer_Confirmation_For_Stock_${selectedVehicle?.stockNumber}.jpeg`;
    const file = base64ToFile(base64Image, filename);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('applicationId', depositPageParams!.applicationId);
      formData.append('downPaymentRequestId', downPaymentDetails!.downPaymentRequestId);
      
      const response = await httpFormDataAsync(
        `DownPayment/upload?clientId=${clientId}`, 
        "POST",
        formData,
        ''
        );
      
      if (response?.ok) {
        console.log('Screenshot uploaded successfully');
      } else {
        console.error('Failed to upload screenshot');
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    }
  };

  const [downPaymentMinutesInterval, setDownPaymentMinutesInterval] = React.useState<number>(2);

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const captureScreenshot = async (): Promise<string | null> => {
    try {
      const container = rootRef.current;
      if (!container) {
        return null;
      }

      // On mobile the inner content container scrolls, so the terms/vehicle info above the
      // fold would be clipped. Capture at full content height by expanding the cloned DOM
      // (the visible page is untouched). The scroller is found by data attribute rather than
      // by its CSS-module class, whose generated hash can contain selector metacharacters
      // (e.g. "…EELK+") — escaping those needs CSS.escape, which is missing in some WebViews.
      const scroller = container.querySelector<HTMLElement>(SCROLLER_SELECTOR);
      const fullHeight = container.scrollHeight
        + (scroller ? scroller.scrollHeight - scroller.clientHeight : 0);

      const canvas = await html2canvas(container, {
        height: fullHeight,
        windowHeight: fullHeight,
        onclone: (_clonedDocument, clonedContainer) => {
          clonedContainer.style.height = 'auto';
          const clonedScroller = clonedContainer.querySelector<HTMLElement>(SCROLLER_SELECTOR);
          if (clonedScroller) {
            clonedScroller.style.overflowY = 'visible';
            clonedScroller.style.height = 'auto';
          }
        }
      });
      return canvas.toDataURL("image/jpeg", 1.0);
    } catch (error) {
      console.error('Error capturing the confirmation screenshot:', error);
      return null;
    }
  };

  const handleGoToPaymentLink = async () => {
    if (acknowledged) {
      // Wait for the checkbox state to render AND for the tap's synthesized mouse events to
      // be processed before html2canvas blocks the main thread — Kendo's draggable throws if
      // its touch/mouse suppression window expires while the event loop is starved.
      await new Promise(resolve => setTimeout(resolve, 400));
      // Capture before switching steps so the acknowledged step-0 content is what's cloned.
      const image = await captureScreenshot();
      setStep(1);
      // Changing the interval to check every 15 seconds because they could have made a payment any time after the button is clicked
      setDownPaymentMinutesInterval(.25);
      if (image) {
        // Upload in the background; the payment view shouldn't wait on the network.
        uploadScreenshot(image);
      }
    }
  }
  
  const getParams = (params: Readonly<Params<string>>): DepositPageParams | null => {
    if ((params as any)?.clientId && (params as any)?.stockNumber && (params as any)?.applicationId && (params as any)?.depositAmount) {
      return {
        clientId: (params as any).clientId,
        customerPortalLinkId: (params as any).customerPortalLinkId,
        stockNumber: (params as any).stockNumber,
        applicationId: (params as any).applicationId,
        depositAmount: +(params as any).depositAmount,
        fullDownPaymentAmount: selectedFilters?.downPayment ?? 0
      };
    } else {
      const decodedParams = base64DecodeParams((params as any)?.encodedUrl);
      if (decodedParams) {
        // Check if the decoded params are valid
        const [customerPortalLinkId, stockNumber, applicationId, depositAmount] = decodedParams;
        return {
          clientId: (params as any).clientId,
          customerPortalLinkId: customerPortalLinkId,
          stockNumber: stockNumber,
          applicationId: applicationId,
          depositAmount: +depositAmount,
          fullDownPaymentAmount: selectedFilters?.downPayment ?? 0
        };
      }
    }
    
    return null;
  }
  
  const [depositPageParams, setDepositPageParams] = React.useState<DepositPageParams | null>(getParams(params));

  // Get clientId, stockNumber, applicationId, and downPayment from the params.
  // If we have all the required params, call the createDownPaymentLink function.
  // If we don't have all the required params, do nothing.
  useEffect(() => {
    if (depositPageParams?.clientId) {
      if (clientId !== depositPageParams.clientId) {
        dispatch(setClient(depositPageParams.clientId));
      }
      dispatch(loadLibrary(depositPageParams.clientId));
      
      const downPaymentLinkRequest: DownPaymentLinkRequest = {
        clientId: depositPageParams.clientId,
        applicationId: depositPageParams.applicationId,
        calculateQuoteAfterCallback: true,
        stockNumber: depositPageParams.stockNumber,
        depositAmount: depositPageParams.depositAmount,
        fullDownPaymentAmount: depositPageParams.fullDownPaymentAmount,
        desiredMonthlyPayment: selectedFilters?.monthlyPayment ?? 0,
        spotVehicleAfterConfirmation: true
      };

      dispatch(createDownPaymentLink(downPaymentLinkRequest));
      dispatch(loadCuratedVehicleDetail(depositPageParams.clientId, null, depositPageParams.stockNumber));
    }
  }, [depositPageParams, clientId, dispatch]);

  // Updates the depositPageParams state when the params change
  useEffect(() => {
    setDepositPageParams(getParams(params));
  }, [params]);

  useEffect(() => {
    if (hasValue(depositPageParams?.applicationId) && hasValue(clientId)) {
      // Once when it loads
      dispatch(getDownPaymentHistories(depositPageParams!.applicationId, clientId!));

      // Repeats every X minutes
      let timer = setInterval(async () => {
        dispatch(getDownPaymentHistories(depositPageParams!.applicationId, clientId!));
      }, downPaymentMinutesInterval * MS_PER_MINUTE); // X minutes
      return () => clearInterval(timer);
    }
  }, [depositPageParams?.applicationId, clientId, downPaymentMinutesInterval]);

  // Redirect to the thank you page if they've already made a payment
  if (downPaymentHistories.length > 0 && depositPageParams) {
    navigate(`/${clientId}/payment-thank-you/${depositPageParams?.customerPortalLinkId}`);
  }

  const VehicleSummaryCards = () => (
    <div className={styles.vehicleCardsContainer}>
      {/* Vehicle Summary Card */}
      <div className={`${styles.infoCard} ${styles.vehicleSummary}`}>
        <h3>Vehicle Summary</h3>
        <div className={styles.vehicleDetails}>
          <div className={styles.detailRow}>
            <strong>Stock #:</strong> {selectedVehicle?.stockNumber}
          </div>
          <div className={styles.detailRow}>
            <strong>Year:</strong> {selectedVehicle?.year}
          </div>
          <div className={styles.detailRow}>
            <strong>Make:</strong> {selectedVehicle?.make}
          </div>
          <div className={styles.detailRow}>
            <strong>Model:</strong> {selectedVehicle?.model}
          </div>
          <div className={styles.detailRow}>
            <strong>VIN:</strong> {selectedVehicle?.vin}
          </div>
        </div>
      </div>

      {/* Down Payment */}
      <div className={`${styles.infoCard} ${styles.downPaymentAmount}`}>
        <h3>Down Payment Amount</h3>
        <div className={`${styles.paymentAmount} ${styles.purple}`}>
          {toDollars(selectedFilters?.downPayment)}
        </div>
      </div>

      {/* Monthly Payment Card */}
      <div className={`${styles.infoCard} ${styles.monthlyPayment}`}>
        <h3>Monthly Payment</h3>
        <div className={`${styles.paymentAmount} ${styles.green}`}>
          {toDollars(selectedFilters?.monthlyPayment)}<span className={styles.perMonth}>/mo</span>
        </div>
      </div>

      {/* Deposit Amount Card */}
      <div className={`${styles.infoCard} ${styles.depositAmount}`}>
        <h3>Deposit Amount</h3>
        <div className={`${styles.paymentAmount} ${styles.orange}`}>
          {toDollars(depositPageParams?.depositAmount)}
        </div>
      </div>
    </div>
  );

  const containerStyle: React.CSSProperties = { height: `calc(100vh - ${props.headerAndFooterHeight}px)` };
  
  // If they got here without setting a down payment, redirect them to the curated page to do so
  if (!hasValue(selectedFilters?.monthlyPayment)) {
    navigate(`/${clientId}/curated/${depositPageParams?.customerPortalLinkId}`);
  }

  return isLoading
    ? (
      <LoadingIndicator label="Loading your page. Just a moment..."/>
    )
    : (
      <div ref={rootRef} className={styles.depositPageContainer} style={containerStyle}>
        {downPaymentDetails && (
          <div className={styles.downPaymentContentContainer} data-deposit-scroller="">
            {step === 1 && (
              <div className={styles.iframeContainer}>
                <h3>Deposit Page</h3>
                <iframe
                  src={downPaymentDetails.link}
                  title={downPaymentDetails.paymentProcessorName}
                />
                <div className={styles.backButtonWrapper}>
                  <Button
                    title="Go back to the prevous page"
                    className={styles.backButton}
                    themeColor={"secondary"}
                    onClick={() => {
                      setStep(0);
                    }}
                  >Back</Button>
                </div>
              </div>
            )}
            {step === 0 && (
              <div className={styles.contentWrapper}>
                {/* Left Column - Vehicle Summary Cards (Desktop) */}
                <div className={styles.vehicleCardsDesktop}>
                  <VehicleSummaryCards />
                </div>

                {/* Mobile Version - Vehicle Summary Cards (shown at top on mobile) */}
                <div className={styles.vehicleCardsMobile}>
                  <VehicleSummaryCards />
                </div>

                {/* Right Column - Main Content */}
                <div className={styles.mainContentColumn}>
                  <div className={styles.mainContentBox}>
                    <h2 className={styles.pageTitle}>
                      Next Steps – Let's Make It Official!
                    </h2>

                    <p className={styles.introText}>
                      We're thrilled to help you lock in your new trailer! Here's what's next:
                    </p>

                    <ul className={styles.stepsList}>
                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          <strong>{'Place your deposit to officially reserve your trailer and take it off the market. This also lets our team start prepping it just for you!'}</strong>
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Your deposit will <strong>go straight toward your down payment</strong>, and you'll have <strong>30 days to complete the process and pick up your new home on wheels.</strong>
                        </div>
                      </li>

                      <li className={`${styles.stepItem} ${styles.warningStep}`}>
                        <span className={`${styles.stepBullet} ${styles.warningBullet}`}></span>
                        <div className={styles.stepContent}>
                          <strong className={styles.warningText}>Heads up:</strong> All deposits are <strong>non-refundable</strong>, so be sure you're ready to move forward and make this yours!
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Once your deposit is in, a team member will reach out shortly to <strong>schedule your pickup day and time.</strong>
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Before pickup, we'll just need proof of <strong>3 months of prepaid insurance</strong> on your trailer.
                        </div>
                      </li>
                    </ul>

                    <p className={styles.contactBox}>
                      If you have any questions or need help with next steps, just give us a shout—we're here and excited to help you hit the road! 🚐✨
                    </p>
                  </div>
                  <div className={styles.actionContainer}>
                    <Checkbox
                      value={acknowledged}
                      label="I understand the deposit is non-refundable and will be applied to my down payment."
                      onChange={(event: CheckboxChangeEvent) => {
                        if (event.value) {
                          setAcknowledged(true);
                        } else {
                          setAcknowledged(false);
                        }
                      }}
                    />
                    <Button
                      className={styles.depositButton}
                      themeColor={"primary"}
                      disabled={!acknowledged}
                      title={`Go to the ${downPaymentDetails.paymentProcessorName} deposit page`}
                      onClick={handleGoToPaymentLink}
                    >Go to Deposit</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <MessageViewer />
      </div>
    );
}